import { useEffect, useRef, useState } from "react";
import type { AssistantAnswer, AssistantState, CvAnalysis, ExtractionLot } from "@shared/modules/x-extraction";
import { ApiError } from "../../../core/lib/api";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Field, Select, Textarea } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { askAssistant, assistantState, listLots } from "../lib/api";
import { formatMonths, fullName, levelLabel } from "../lib/profile";
import { ProfileModal } from "../components/profile-modal";

interface Exchange {
  question: string;
  answer: AssistantAnswer;
}

const ERRORS: Record<string, string> = {
  assistant_indisponible: "L'assistant n'est pas disponible (clé Anthropic absente ou service injoignable).",
  assistant_refus: "L'assistant a décliné cette demande. Reformulez-la.",
  assistant_reponse_invalide: "La réponse de l'assistant était incomplète. Réessayez.",
};

const EXAMPLES = [
  "Qui est le plus apte pour un poste de soudeur TIG en chantier naval ?",
  "Quels candidats ont au moins 5 ans d'expérience et parlent anglais ?",
  "Classe les profils les plus solides pour de la maintenance électrique industrielle.",
];

function scoreVariant(score: number): "success" | "info" | "warning" {
  return score >= 75 ? "success" : score >= 50 ? "info" : "warning";
}

/**
 * Assistant de recherche : on décrit un poste ou une tâche, il classe les
 * profils du vivier et justifie. Seuls des profils anonymisés quittent le
 * serveur ; les noms sont remis à l'écran par le serveur.
 */
export function AssistantPage() {
  const [state, setState] = useState<AssistantState | null>(null);
  const [lots, setLots] = useState<ExtractionLot[]>([]);
  const [lotId, setLotId] = useState("");
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState<Exchange[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CvAnalysis | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    assistantState()
      .then(setState)
      .catch(() => setState({ available: false, model: "" }));
    listLots()
      .then(setLots)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread.length, busy]);

  const ask = async (text: string) => {
    const q = text.trim();
    if (q.length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      // L'historique renvoyé est la version anonymisée (références P1…), jamais les noms affichés.
      const history = thread.flatMap((x) => [
        { role: "user" as const, content: x.answer.anonymizedQuestion },
        { role: "assistant" as const, content: x.answer.anonymizedAnswer },
      ]);
      const answer = await askAssistant({ question: q, lotId: lotId || null, history: history.slice(-20) });
      setThread([...thread, { question: q, answer }]);
      setQuestion("");
    } catch (err) {
      setError(err instanceof ApiError ? (ERRORS[err.code] ?? "Question impossible pour le moment.") : "Question impossible pour le moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Assistant"
        subtitle="Décrivez un poste ou une tâche : l'assistant classe les CV du vivier et explique pourquoi."
        actions={
          thread.length ? (
            <Button variant="ghost" onClick={() => setThread([])}>
              Nouvelle conversation
            </Button>
          ) : undefined
        }
      />

      <p className="assistant__privacy">
        <Icon name="shield" size={16} />
        <span>
          Seuls des profils <strong>anonymisés</strong> sont transmis à Claude ({state?.model || "Anthropic"}) : intitulés de poste, durées, compétences, diplômes et langues, sous une
          référence. Jamais de nom, de coordonnées, d'employeur, d'établissement, de ville ni de nationalité. Les noms cités dans une question sont remplacés avant l'envoi.
        </span>
      </p>

      {state && !state.available ? (
        <Card title="Assistant non configuré">
          <p className="muted">La clé de l'API Anthropic (ANTHROPIC_API_KEY) n'est pas posée sur ce serveur. Le reste de l'application fonctionne normalement.</p>
        </Card>
      ) : null}

      <div className="assistant__thread">
        {thread.length === 0 && state?.available ? (
          <div className="assistant__examples">
            <p className="muted">Exemples :</p>
            {EXAMPLES.map((e) => (
              <button key={e} type="button" className="lot-stats__tag" onClick={() => setQuestion(e)}>
                {e}
              </button>
            ))}
          </div>
        ) : null}

        {thread.map((x, i) => (
          <div key={i} className="assistant__exchange">
            <p className="assistant__question">{x.question}</p>
            <Card title="Réponse" subtitle={`${x.answer.consulted} profil${x.answer.consulted > 1 ? "s" : ""} consulté${x.answer.consulted > 1 ? "s" : ""}`}>
              <p className="assistant__answer">{x.answer.answer}</p>
              {x.answer.candidates.length ? (
                <ol className="assistant__candidates">
                  {x.answer.candidates.map((c) => {
                    const p = c.analysis.profile;
                    return (
                      <li key={c.analysis.jobId} className="assistant__candidate">
                        <div className="assistant__candidate-head">
                          <span className="assistant__rank">{c.rank}</span>
                          <button type="button" className="analyses__name" onClick={() => setSelected(c.analysis)}>
                            {fullName(c.analysis)}
                          </button>
                          <Badge variant={scoreVariant(c.score)}>{c.score} / 100</Badge>
                        </div>
                        <p className="assistant__facts">
                          {[p?.title ?? p?.totals.lastTitle, formatMonths(p?.totals.experienceMonths), levelLabel(p?.totals.highestLevel)].filter((v) => v && v !== "—").join(" · ")}
                        </p>
                        <div className="assistant__why">
                          <div>
                            <h4 className="profil__heading">Atouts</h4>
                            <ul className="assistant__reasons">
                              {c.reasons.map((r) => (
                                <li key={r}>{r}</li>
                              ))}
                            </ul>
                          </div>
                          {c.gaps.length ? (
                            <div>
                              <h4 className="profil__heading">À vérifier</h4>
                              <ul className="assistant__gaps">
                                {c.gaps.map((g) => (
                                  <li key={g}>{g}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : null}
            </Card>
          </div>
        ))}
        {busy ? <p className="muted">L'assistant compare les profils…</p> : null}
        <div ref={endRef} />
      </div>

      {state?.available ? (
        <form
          className="assistant__form"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(question);
          }}
        >
          <Field label={thread.length ? "Question suivante" : "Votre demande"} hint="Ex. un poste, des compétences, une langue, une durée d'expérience. N'y mettez pas de critère discriminatoire.">
            <Textarea
              rows={3}
              value={question}
              maxLength={2000}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void ask(question);
              }}
            />
          </Field>
          <div className="assistant__actions">
            <Field label="Chercher dans">
              <Select value={lotId} onChange={(e) => setLotId(e.target.value)} disabled={thread.length > 0}>
                <option value="">Tout le vivier</option>
                {lots.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
            </Field>
            {error ? <p className="assistant__error">{error}</p> : null}
            <Button type="submit" variant="primary" disabled={busy || question.trim().length < 3} icon={<Icon name="sparkles" size={16} />}>
              Demander
            </Button>
          </div>
        </form>
      ) : null}

      <ProfileModal analysis={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
